import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Upload, Search, ArrowLeft, User, LogOut, ChevronDown, Settings } from 'lucide-react';
import Sidebar from './components/Sidebar';
import Home from './components/Home';
import Library from './components/Library';
import Chats from './components/Chats';
import Summaries from './components/Summaries';
import DocumentAnnotator from './components/DocumentAnnotator';
import Deals from './components/Deals';
import Contacts from './components/Contacts';
import AIFindings from './components/AIFindings';
import FinancialsPage from './components/FinancialsPage';
import FindingsNotes from './components/FindingsNotes';

import DocumentUpload from './components/DocumentUpload';
import DiligenceTracker from './components/DiligenceTracker';
import Pipeline from './components/Pipeline';
import SettingsPage from './components/SettingsPage';
import GuidePage from './components/GuidePage';
import CRM from './components/CRM';
import Tasks from './components/Tasks';
import DealAnalytics from './components/DealAnalytics';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import PlatformAdmin from './pages/PlatformAdmin';
import InvitePage from './pages/InvitePage';
import PortalAcceptPage from './pages/PortalAcceptPage';
import PortalPage from './pages/PortalPage';
import CpaReviewerPage from './pages/CpaReviewerPage';
import TermsPage from './pages/TermsPage';
import ApproveUserPage from './pages/ApproveUserPage';
import AuthCallback from './pages/AuthCallback';
import axios from 'axios';
import { can, hasPIAccess } from './lib/permissions';
import { supabase, buildCurrentUser, getOrgById, clearUserCache } from './lib/supabase';
import OrgSwitcher from './components/OrgSwitcher';
import { dealsApi, setApiToken } from './services/api';
import PIShell from './components/pi/PIShell';
import GrataIntegration from './components/GrataIntegration';
import { AnalysisProvider, useAnalysis } from './contexts/AnalysisContext';

import './App.css';
import './styles/dealnexa-theme.css';

// Use environment variable; empty string means "same host" (React proxy handles it in dev)
export const API_URL = process.env.REACT_APP_API_URL || '';

// Enable cookies for cross-origin requests (required for embed session)
axios.defaults.withCredentials = true;

// View <-> URL path mapping
const VIEW_TO_PATH = {
  home:             '/home',
  library:          '/library',
  chats:            '/chats',
  summaries:        '/summaries',
  annotations:      '/annotations',
  contacts:         '/contacts',
  findings:         '/findings',
  pipeline:         '/pipeline',
  tasks:            '/tasks',
  deals:            '/deals',
  aifindings:       '/ai-findings',
  financials:       '/financials',
  diligencetracker: '/diligence',
  settings:         '/settings',
  crm:              '/crm',
  analytics:        '/analytics',
  guide:            '/guide',
  upload:           '/upload',
  grata:            '/grata',
};
const PATH_TO_VIEW = Object.fromEntries(Object.entries(VIEW_TO_PATH).map(([v, p]) => [p, v]));

// Main App Content Component (for authenticated users)
function AppContent({ currentUser, onLogout, isEmbed: propIsEmbed }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive initial view from URL path
  const viewFromPath = PATH_TO_VIEW[location.pathname] || 'home';
  const [currentView, setCurrentView] = useState(viewFromPath);

  // Keep currentView in sync when user uses browser back/forward
  useEffect(() => {
    const view = PATH_TO_VIEW[location.pathname];
    if (view && view !== currentView) {
      setCurrentView(view);
    }
    // Sync piMode from URL (e.g., direct /pi/* navigation or browser back)
    const isOnPI = location.pathname.startsWith('/pi/');
    setPiMode(prev => {
      if (isOnPI && !prev && hasPIAccess(currentUser)) return true;
      if (!isOnPI && prev) return false;
      return prev;
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [deals, setDeals] = useState([]);
  const [dealToOpen, setDealToOpen] = useState(null);
  const [dealInitialTab, setDealInitialTab] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [orgData, setOrgData] = useState(null);
  
  // Embed mode state
  const [isEmbed, setIsEmbed] = useState(false);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState(null);
  const [embedDeal, setEmbedDeal] = useState(null);
  const [embedReturnUrl, setEmbedReturnUrl] = useState('');

  // Load org data when user has an org
  useEffect(() => {
    if (currentUser?.orgId) {
      // Use org data from user if available, otherwise fetch from API
      if (currentUser.orgName) {
        setOrgData({
          id: currentUser.orgId,
          name: currentUser.orgName,
          type: 'vc' // Default type, can be updated from API
        });
      }
      // Also try to fetch full org details from API
      getOrgById(currentUser.orgId).then(org => { 
        if (org) setOrgData(org); 
      }).catch(() => {});
    }
  }, [currentUser?.orgId, currentUser?.orgName]);

  // Audit log helper — fire-and-forget
  const logAudit = useCallback((action, resource, details) => {
    if (!currentUser) return;
    axios.post(`${API_URL}/api/audit/log`, {
      action,
      resource: resource || '',
      details: details || '',
      userName: currentUser.name || currentUser.email,
    }).catch(() => {});
  }, [currentUser]);

  // Fetch deals — callable from child components too
  const fetchDeals = useCallback(async () => {
    try {
      const response = await dealsApi.list();
      setDeals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  // Check for embed mode on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isEmbedMode = propIsEmbed || urlParams.get('embed') === 'true' || window.location.pathname === '/embed' || urlParams.get('token') !== null;
    
    if (isEmbedMode) {
      setIsEmbed(true);
      handleEmbedAuth();
    }
    
    // Listen for embed config from parent (if in iframe)
    if (window.DEALNEXA_EMBED) {
      setEmbedReturnUrl(window.DEALNEXA_EMBED.returnUrl || '');
    }
  }, []);
  
  // Handle embed authentication
  const handleEmbedAuth = async () => {
    const token = new URLSearchParams(window.location.search).get('token');
    const externalDealId = new URLSearchParams(window.location.search).get('externalDealId');
    
    // First, try to use existing session (for "Open in new tab" scenarios)
    // by checking if we can fetch deals (which requires valid session)
    setEmbedLoading(true);
    try {
      // Try to fetch deals - this will only succeed if session cookie is valid
      const dealsResponse = await axios.get(`${API_URL}/api/deals`);
      
      // If we got here, session exists and is valid
      if (dealsResponse.data && dealsResponse.data.length > 0) {
        // Find the deal matching the externalDealId from URL
        let targetDeal = null;
        if (externalDealId) {
          targetDeal = dealsResponse.data.find(d => d.externalDealId === externalDealId);
        }
        
        // If we found the matching deal in the current session, use it
        if (targetDeal) {
          console.log('[Embed] Using existing session for deal:', targetDeal.name);
          setEmbedDeal(targetDeal);
          setSelectedDeal(targetDeal);
          setCurrentView('home');
          
          // Notify parent
          if (window.parent !== window) {
            window.parent.postMessage({
              type: 'dealnexa.ready',
              dealId: targetDeal.id
            }, '*');
          }
          setEmbedLoading(false);
          return; // Skip SSO exchange, we have a valid session for this deal
        }
        
        // Deal not found in current session - we need to do SSO exchange
        // if we have a token for the new deal
        if (externalDealId && token) {
          console.log('[Embed] Deal not in session, doing SSO exchange for:', externalDealId);
          // Continue to SSO exchange below (don't return)
        } else {
          // No token to exchange, use the first deal from session as fallback
          console.log('[Embed] No token for new deal, using session deal:', dealsResponse.data[0].name);
          setEmbedDeal(dealsResponse.data[0]);
          setSelectedDeal(dealsResponse.data[0]);
          setCurrentView('home');
          
          if (window.parent !== window) {
            window.parent.postMessage({
              type: 'dealnexa.ready',
              dealId: dealsResponse.data[0].id
            }, '*');
          }
          setEmbedLoading(false);
          return;
        }
      }
    } catch (sessionError) {
      // No valid session, continue to SSO exchange below
      console.log('[Embed] No valid session, trying SSO exchange...');
    }
    
    // If we have a token and no valid session, exchange it
    if (token) {
      try {
        const response = await axios.post(`${API_URL}/api/partner/sso/exchange`, {
          token,
          externalDealId
        });
        
        // Load the deal
        const dealResponse = await axios.get(`${API_URL}/api/partner/deal/${response.data.dealId}`);
        setEmbedDeal(dealResponse.data);
        setSelectedDeal(dealResponse.data);
        
        // Go to home page to show full platform
        setCurrentView('home');
        
        // Notify parent
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'dealnexa.ready',
            dealId: response.data.dealId,
            externalDealId: dealResponse.data.externalDealId
          }, '*');
        }
        
      } catch (error) {
        console.error('Embed auth failed:', error);
        setEmbedError(error.response?.data?.error || 'Authentication failed');
      } finally {
        setEmbedLoading(false);
      }
    } else {
      // No token and no session
      setEmbedError('No authentication token provided');
      setEmbedLoading(false);
    }
  };

  const [openCreateDeal, setOpenCreateDeal] = useState(false);
  const [piMode, setPiMode] = useState(false);
  const [lastDDPath, setLastDDPath] = useState('/home');

  // Handle navigate to PI module
  const handleNavigateToPI = () => {
    if (!hasPIAccess(currentUser)) return;
    setLastDDPath(location.pathname || '/home');
    setPiMode(true);
    navigate('/pi/dashboard');
  };

  const handleBackToDD = (dealId) => {
    setPiMode(false);
    if (dealId) {
      // Find the deal and open it
      const deal = deals.find(d => d.id === dealId);
      if (deal) { setDealToOpen(deal); setCurrentView('deals'); }
    }
    navigate(lastDDPath || '/home');
  };

  // Listen for PI → DD navigation events (e.g. from launch DD in Add-On tab)
  useEffect(() => {
    const handler = (e) => {
      const dealId = e.detail?.dealId || sessionStorage.getItem('dealnexa_open_deal_id');
      sessionStorage.removeItem('dealnexa_open_deal_id');
      handleBackToDD(dealId);
    };
    window.addEventListener('dealnexa:exit-pi', handler);
    // Also check sessionStorage on mount (handles window.location.assign case)
    const pendingDealId = sessionStorage.getItem('dealnexa_open_deal_id');
    if (pendingDealId) {
      sessionStorage.removeItem('dealnexa_open_deal_id');
      const deal = deals.find(d => d.id === pendingDealId);
      if (deal) { setDealToOpen(deal); setPiMode(false); navigate('/deals'); }
    }
    return () => window.removeEventListener('dealnexa:exit-pi', handler);
  }, [deals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle navigation
  const handleNavigate = (view, opts = {}) => {
    setCurrentView(view);
    const path = VIEW_TO_PATH[view];
    if (path && location.pathname !== path) {
      navigate(path);
    }
    // Reset selections when navigating away
    if (view !== 'annotations') {
      setSelectedDocumentId(null);
    }
    if (opts.openCreate) {
      setOpenCreateDeal(true);
    }
  };

  // Handle navigate to deal from contacts
  const handleNavigateToDeal = (dealId) => {
    const deal = deals.find(d => d.id === dealId);
    if (deal) {
      setSelectedDeal(deal);
      handleNavigate('deals');
    }
  };

  // Handle opening a document for annotation
  const handleOpenAnnotate = (documentId, annotationId = null) => {
    setSelectedDocumentId(documentId);
    setSelectedAnnotationId(annotationId);
    handleNavigate('annotations');
  };

  // Handle navigating to a task's origin
  const handleNavigateToTask = (task) => {
    // Check for annotation_link in description (regardless of task.type)
    try {
      const parsed = JSON.parse(task.description || '{}');
      if (parsed.type === 'annotation_link' && parsed.documentId) {
        handleOpenAnnotate(parsed.documentId, parsed.annotationId || null);
        return;
      }
    } catch {}

    if (task.type === 'diligence') {
      const deal = deals.find(d => d.id === task.dealId);
      if (deal) {
        setSelectedDeal(deal);
        handleNavigate('diligencetracker');
      }
    } else {
      // task or irl → open deal detail at the relevant tab
      const deal = deals.find(d => d.id === task.dealId);
      if (deal) {
        setDealInitialTab(task.type === 'irl' ? 'irl' : 'tasks');
        setDealToOpen(deal);
        handleNavigate('deals');
      }
    }
  };

  // Handle viewing Diligence Tracker for a deal
  const handleViewDiligenceTracker = (deal) => {
    setSelectedDeal(deal);
    handleNavigate('diligencetracker');
  };

  // Handle analysis completion from upload
  const handleAnalysisComplete = () => {
    console.log('Analysis complete');
    
    // Notify parent if in embed mode
    if (isEmbed && window.parent !== window && selectedDeal) {
      window.parent.postMessage({
        type: 'dealnexa.analysis.complete',
        dealId: selectedDeal.id,
        externalDealId: selectedDeal.externalDealId
      }, '*');
    }
  };

  // Handle back button in embed mode
  const handleEmbedBack = () => {
    if (embedReturnUrl) {
      window.location.href = embedReturnUrl;
    } else if (window.parent !== window) {
      window.parent.postMessage({ type: 'dealnexa.back' }, '*');
    }
  };

  const AccessDenied = () => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'60vh', color:'#64748b', gap:'12px' }}>
      <span style={{ fontSize:'2.5rem' }}>🔒</span>
      <h2 style={{ margin:0, color:'#1e2536' }}>Access Restricted</h2>
      <p style={{ margin:0 }}>You don't have permission to view this section.</p>
    </div>
  );

  // Render the current view
  const renderView = () => {
    switch (currentView) {
      case 'home':
        return (
          <Home
            onNavigate={handleNavigate}
            onViewDiligenceTracker={handleViewDiligenceTracker}
            onSelectDeal={setSelectedDeal}
            currentUser={currentUser}
            onNavigateToTask={handleNavigateToTask}
          />
        );
      case 'library':
        if (!can(currentUser, 'view_documents')) return <AccessDenied />;
        return <Library onOpenAnnotate={handleOpenAnnotate} canEdit={can(currentUser, 'edit')} canDownload={can(currentUser, 'download')} />;
      case 'chats':
        return <Chats />;
      case 'summaries':
        if (!can(currentUser, 'view_documents')) return <AccessDenied />;
        return <Summaries onOpenAnnotate={handleOpenAnnotate} currentUser={currentUser} />;
      case 'annotations':
        if (!can(currentUser, 'view_documents')) return <AccessDenied />;
        return <DocumentAnnotator key={selectedDocumentId} documentId={selectedDocumentId} dealId={selectedDeal?.id || embedDeal?.id} canEdit={can(currentUser, 'edit')} initialAnnotationId={selectedAnnotationId} />;
      case 'contacts':
        return <Contacts onNavigateToDeal={handleNavigateToDeal} currentUser={currentUser} />;
      case 'findings':
        if (!can(currentUser, 'view_deals')) return <AccessDenied />;
        return <FindingsNotes canEdit={can(currentUser, 'edit')} canDownload={can(currentUser, 'download')} logAudit={logAudit} />;
      case 'tasks':
        return <Tasks currentUser={currentUser} onNavigateToTask={handleNavigateToTask} />;
      case 'pipeline':
        if (!can(currentUser, 'view_deals')) return <AccessDenied />;
        return (
          <Pipeline
            onNavigate={handleNavigate}
            deals={deals}
            setDeals={setDeals}
            onOpenDeal={(deal) => { setDealToOpen(deal); handleNavigate('deals'); }}
            currentUser={currentUser}
          />
        );
      case 'deals':
        if (!can(currentUser, 'view_deals')) return <AccessDenied />;
        return (
          <Deals
            onViewDiligenceTracker={handleViewDiligenceTracker}
            deals={deals}
            setDeals={setDeals}
            initialDeal={dealToOpen}
            initialTab={dealInitialTab}
            onInitialDealConsumed={() => { setDealToOpen(null); setDealInitialTab(null); }}
            openCreate={openCreateDeal}
            onOpenCreateConsumed={() => setOpenCreateDeal(false)}
            currentUser={currentUser}
            logAudit={logAudit}
          />
        );
      case 'aifindings':
        if (!can(currentUser, 'view_deals')) return <AccessDenied />;
        return <AIFindings selectedDeal={selectedDeal || embedDeal} currentUser={currentUser} />;
      case 'financials':
        if (!can(currentUser, 'view_deals')) return <AccessDenied />;
        return <FinancialsPage selectedDeal={selectedDeal || embedDeal} currentUser={currentUser} />;
      case 'diligencetracker':
        if (!can(currentUser, 'view_deals')) return <AccessDenied />;
        return <DiligenceTracker selectedDeal={selectedDeal || embedDeal} currentUser={currentUser} />;
      case 'settings':
        return (
          <SettingsPage
            currentUser={currentUser}
            orgData={orgData}
            onNavigate={handleNavigate}
          />
        );
      case 'crm':
        return (
          <CRM
            deals={deals}
            setDeals={setDeals}
            fetchDeals={fetchDeals}
            logAudit={logAudit}
            currentUser={currentUser}
          />
        );
      case 'analytics':
        return <DealAnalytics currentUser={currentUser} />;
      case 'guide':
        return <GuidePage />;
      case 'upload':
        if (!can(currentUser, 'view_documents')) return <AccessDenied />;
        return (
          <div className="upload-view">
            <div className="page-header">
              <div className="page-title">
                <div className="page-title-icon"><Upload size={20} /></div>
                <h1>Upload Documents</h1>
              </div>
            </div>
            <DocumentUpload onAnalysisComplete={handleAnalysisComplete} />
          </div>
        );
      case 'grata':
        return <GrataIntegration currentUser={currentUser} />;
      default:
        return (
          <Home 
            onNavigate={handleNavigate} 
            onViewDiligenceTracker={handleViewDiligenceTracker}
          />
        );
    }
  };

  // Embed mode loading state
  if (isEmbed && embedLoading) {
    return (
      <div className="embed-loading">
        <div className="spinner"></div>
        <p>Loading DealNexa...</p>
      </div>
    );
  }

  // Embed mode error state
  if (isEmbed && embedError) {
    return (
      <div className="embed-error">
        <h2>Failed to load</h2>
        <p>{embedError}</p>
        {embedReturnUrl && (
          <button onClick={handleEmbedBack} className="btn-primary">
            <ArrowLeft size={16} /> Go Back
          </button>
        )}
      </div>
    );
  }

  // Embed mode - show full platform with navigation
  if (isEmbed) {
    return (
      <div className="app embed-mode">
        {/* Embed header with navigation */}
        <header className="embed-header">
          <div className="embed-header-left">
            {embedReturnUrl && (
              <button onClick={handleEmbedBack} className="embed-back-btn">
                <ArrowLeft size={16} /> Back
              </button>
            )}
            <span className="embed-deal-name">
              {embedDeal?.name || selectedDeal?.name || 'DealNexa'}
            </span>
          </div>
          
          {/* Navigation tabs */}
          <nav className="embed-nav">
            <button 
              className={`embed-nav-btn ${currentView === 'home' ? 'active' : ''}`}
              onClick={() => handleNavigate('home')}
            >
              Dashboard
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'deals' ? 'active' : ''}`}
              onClick={() => handleNavigate('deals')}
            >
              Deals
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'library' ? 'active' : ''}`}
              onClick={() => handleNavigate('library')}
            >
              Library
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'diligencetracker' ? 'active' : ''}`}
              onClick={() => handleNavigate('diligencetracker')}
            >
              Tracker
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'aifindings' ? 'active' : ''}`}
              onClick={() => handleNavigate('aifindings')}
            >
              AI Findings
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'annotations' ? 'active' : ''}`}
              onClick={() => handleNavigate('annotations')}
            >
              Annotations
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'chats' ? 'active' : ''}`}
              onClick={() => handleNavigate('chats')}
            >
              Chat
            </button>
            <button 
              className={`embed-nav-btn ${currentView === 'upload' ? 'active' : ''}`}
              onClick={() => handleNavigate('upload')}
            >
              Upload
            </button>
          </nav>
          
          <div className="embed-header-right">
            <span className="embed-badge">Due Diligence</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="embed-content">
          <ErrorBoundary label="This view">
            {renderView()}
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  // PI Module mode — full-screen PI Shell
  if (piMode && hasPIAccess(currentUser)) {
    return (
      <PIShell
        currentUser={currentUser}
        onBackToDD={handleBackToDD}
        lastDDPath={lastDDPath}
      />
    );
  }

  // Full app mode
  return (
    <div className="app">
      <Sidebar activeView={currentView} onNavigate={handleNavigate} orgName={orgData?.name} currentUser={currentUser} onNavigateToPI={handleNavigateToPI} />
      
      <div className="main-wrapper">
        {/* Top Header */}
        <header className="top-header">
          <div className="sidebar-header-mobile">
            <span className="sidebar-logo">{orgData?.name ? orgData.name.slice(0, 2).toUpperCase() : 'DN'}</span>
            <span className="sidebar-brand">{orgData?.name || 'DealNexa'}</span>
          </div>
          
          <div className="header-search">
            <div className="search-input-wrapper">
              <span className="search-icon"><Search size={16} /></span>
              <input 
                type="text" 
                className="search-input" 
                placeholder="Search documents, deals, annotations..."
              />
            </div>
          </div>
          
          <div className="header-actions">
            <OrgSwitcher />
            {currentUser && (
              <div className="user-menu-container">
                <button 
                  className="user-avatar-btn"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <span className="user-avatar">
                    {currentUser?.name?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || 'U'}
                  </span>
                  <ChevronDown size={14} />
                </button>
                
                {showUserDropdown && (
                  <>
                    <div className="user-dropdown-overlay" onClick={() => setShowUserDropdown(false)} />
                    <div className="user-dropdown">
                      <div className="user-dropdown-header">
                        <span className="user-dropdown-name">{currentUser.name || currentUser.email}</span>
                        <span className="user-dropdown-org">{currentUser.orgName || (currentUser.orgId ? 'Organization Member' : 'Personal Account')}</span>
                      </div>
                      <div className="user-dropdown-divider" />
                      <button
                        className="user-dropdown-item"
                        onClick={() => {
                          setShowUserDropdown(false);
                          setShowProfileModal(true);
                        }}
                      >
                        <User size={16} />
                        <span>Profile</span>
                      </button>
                      <button
                        className="user-dropdown-item"
                        onClick={() => {
                          setShowUserDropdown(false);
                          handleNavigate('settings');
                        }}
                      >
                        <Settings size={16} />
                        <span>Settings</span>
                      </button>
                      <div className="user-dropdown-divider" />
                      <button
                        className="user-dropdown-item logout-item"
                        onClick={onLogout}
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <ErrorBoundary label="This view">
            {renderView()}
          </ErrorBoundary>
        </main>
        
        {/* Global analysis progress toast */}
        <AnalysisToast />

        {/* Profile Modal */}
        {showProfileModal && (
          <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
            <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Profile</h2>
                <button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="profile-section">
                  <div className="profile-avatar-large">
                    {currentUser?.name?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="profile-info">
                    <div className="profile-field">
                      <label>Name</label>
                      <span>{currentUser.name || 'Not set'}</span>
                    </div>
                    <div className="profile-field">
                      <label>Email</label>
                      <span>{currentUser.email}</span>
                    </div>
                    <div className="profile-field">
                      <label>Organization</label>
                      <span>{currentUser.orgName || (currentUser.orgId ? 'Organization Member' : 'Personal Account')}</span>
                    </div>
                    <div className="profile-field">
                      <label>User ID</label>
                      <span className="profile-id">{currentUser.id}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowProfileModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
        

      </div>
    </div>
  );
}

// Floating toast shown on any page while analysis is running
// Map (status, stage message) → segment id. Pure function, no side effects.
function getActiveSegment(status, stage) {
  const s = (stage || '').toLowerCase();
  if (s.includes('cleaning layer')) return 'cleaning';
  if (s.includes('synthesiz') || s.includes('consolidat')) return 'synthesizing';
  if (status === 'saving' || s.includes('saving')) return 'saving';
  if (status === 'analyzing' || s.includes('analyz') || s.includes('batch') || s.includes('retry')) return 'analyzing';
  return null; // loading / unknown
}

const SEGMENT_ORDER = ['cleaning', 'analyzing', 'synthesizing', 'saving'];

function AnalysisToast() {
  const { state, cancelAnalysis } = useAnalysis();
  const isActive = state.status !== 'idle' && state.status !== 'complete' && state.status !== 'error';
  const isDone   = state.status === 'complete';
  const isError  = state.status === 'error';

  // Track whether a cleaning phase was detected in this run (shows/hides that segment).
  const [hasCleaningPhase, setHasCleaningPhase] = React.useState(false);
  // Track the furthest segment reached so segments stay complete when we move forward.
  const [maxSegmentIdx, setMaxSegmentIdx] = React.useState(-1);
  const prevDealId = React.useRef(null);

  React.useEffect(() => {
    // Reset on new analysis run.
    if (state.dealId !== prevDealId.current) {
      setHasCleaningPhase(false);
      setMaxSegmentIdx(-1);
      prevDealId.current = state.dealId;
    }
  }, [state.dealId]);

  const activeSeg = getActiveSegment(state.status, state.stage);

  React.useEffect(() => {
    if (!activeSeg) return;
    if (activeSeg === 'cleaning') setHasCleaningPhase(true);
    const idx = SEGMENT_ORDER.indexOf(activeSeg);
    if (idx > -1) setMaxSegmentIdx(prev => Math.max(prev, idx));
  }, [activeSeg]);

  if (state.status === 'idle') return null;

  // Build the segment list for this run.
  const segments = [
    ...(hasCleaningPhase ? [{ id: 'cleaning', label: 'Cleaning' }] : []),
    { id: 'analyzing',    label: 'Analyzing'    },
    { id: 'synthesizing', label: 'Synthesizing' },
    { id: 'saving',       label: 'Saving'        },
  ];

  const activeIdx  = SEGMENT_ORDER.indexOf(activeSeg);
  // A segment is complete when we've moved strictly past it.
  const segStatus = (id) => {
    const segIdx = SEGMENT_ORDER.indexOf(id);
    if (isDone) return 'complete';
    if (segIdx < activeIdx) return 'complete';
    if (id === activeSeg) return 'active';
    return 'pending';
  };

  return (
    <div className={`analysis-toast ${isActive ? 'running' : isDone ? 'done' : 'error'}`}>
      <div className="analysis-toast-header">
        <div className="analysis-toast-left">
          {isActive && <span className="analysis-toast-spinner" />}
          {isDone  && <span className="analysis-toast-icon">✓</span>}
          {isError && <span className="analysis-toast-icon error">!</span>}
          <div className="analysis-toast-text">
            <span className="analysis-toast-title">
              {isDone ? 'Analysis complete' : isError ? 'Analysis failed' : 'Analysis running'}
            </span>
            {state.dealName && (
              <span className="analysis-toast-deal">{state.dealName}</span>
            )}
          </div>
        </div>
        <button
          className="analysis-toast-cancel"
          onClick={cancelAnalysis}
          title={isActive ? 'Cancel analysis' : 'Dismiss'}
        >✕</button>
      </div>

      {/* Segmented step track — shown during active analysis */}
      {isActive && segments.length > 0 && (
        <div className="analysis-toast-track">
          {segments.map((seg, i) => {
            const st = segStatus(seg.id);
            return (
              <React.Fragment key={seg.id}>
                <div className="toast-step">
                  <div className={`toast-step-dot toast-step-dot--${st}`}>
                    {st === 'complete' && <span className="toast-step-check">✓</span>}
                    {st === 'active'   && <span className="toast-step-pulse" />}
                  </div>
                  <span className={`toast-step-label toast-step-label--${st}`}>{seg.label}</span>
                </div>
                {i < segments.length - 1 && (
                  <div className={`toast-step-line ${st === 'complete' ? 'toast-step-line--filled' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Current stage message */}
      {isActive && state.stage && (
        <div className="analysis-toast-stage-row">
          <span className="analysis-toast-stage">{state.stage}</span>
        </div>
      )}
      {isError && state.error && (
        <div className="analysis-toast-stage-row">
          <span className="analysis-toast-stage">{state.error}</span>
        </div>
      )}
    </div>
  );
}

// Polls /api/auth/me every 10s — redirects as soon as admin approves
function PendingApprovalScreen({ email, accessToken, onApproved, onSignOut }) {
  const API_URL = process.env.REACT_APP_API_URL || '';
  React.useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (res.ok) onApproved(); // approved — reload into the app
      } catch (_) {}
    };
    const interval = setInterval(check, 10000);
    return () => clearInterval(interval);
  }, [accessToken, onApproved]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'sans-serif' }}>
      <div style={{ maxWidth: 420, textAlign: 'center', padding: '48px 32px', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <h2 style={{ margin: '0 0 12px', color: '#0f172a', fontSize: 22 }}>Account Pending Approval</h2>
        <p style={{ color: '#64748b', lineHeight: 1.6, margin: '0 0 8px' }}>
          Your account is under review. You'll receive an email at <strong>{email || 'your address'}</strong> once approved.
        </p>
        <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 28px' }}>This page checks automatically — no need to refresh.</p>
        <button
          onClick={onSignOut}
          style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', color: '#64748b', fontSize: 14 }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

// Main App Component with Router
function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(null); // 'pending' | 'rejected' | null
  const axiosInterceptorRef = useRef(null);

  // Check if this is an embed session (external platform user)
  const isEmbed = window.location.pathname === '/embed' ||
    new URLSearchParams(window.location.search).get('embed') === 'true' ||
    new URLSearchParams(window.location.search).get('token') !== null;

  // Register the axios Authorization interceptor immediately (synchronous, not in useEffect)
  // so the token is available before any child component fires its first request.
  const applyToken = useCallback((token) => {
    // Update the api.js module-level store (covers all axios.create() instances)
    setApiToken(token);
    // Also update the global axios interceptor (covers direct axios calls)
    if (axiosInterceptorRef.current !== null) {
      axios.interceptors.request.eject(axiosInterceptorRef.current);
    }
    axiosInterceptorRef.current = axios.interceptors.request.use((config) => {
      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });
    setAccessToken(token);
  }, []);

  // Supabase session listener — single source of truth for auth state
  useEffect(() => {
    if (isEmbed) {
      setAuthChecked(true);
      return;
    }

    // Use getSession() to get a valid (auto-refreshed) token on page load.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user && session?.access_token) {
        applyToken(session.access_token);
        buildCurrentUser(session.user, session.access_token)
          .then(user => { setApprovalStatus(null); setCurrentUser(user); })
          .catch(err => {
            if (err.code === 'PENDING_APPROVAL') { setApprovalStatus('pending'); }
            else if (err.code === 'ACCOUNT_REJECTED') { setApprovalStatus('rejected'); }
            else setCurrentUser({ id: session.user.id, email: session.user.email, name: session.user.user_metadata?.full_name || '', orgId: null, role: 'member' });
          })
          .finally(() => setAuthChecked(true));
      } else {
        setAuthChecked(true);
      }
    }).catch(() => setAuthChecked(true));

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
        applyToken(null);
      } else if (event !== 'INITIAL_SESSION' && session?.user) {
        applyToken(session.access_token);
        try {
          const user = await buildCurrentUser(session.user, session.access_token);
          setApprovalStatus(null);
          setCurrentUser(user);
        } catch (err) {
          if (err.code === 'PENDING_APPROVAL') setApprovalStatus('pending');
          else if (err.code === 'ACCOUNT_REJECTED') setApprovalStatus('rejected');
        }
      }
    });

    // Refresh session when user returns to the tab after being idle
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (!session) {
            setCurrentUser(null);
            applyToken(null);
          }
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isEmbed]);

  // Called by LoginPage / SignupPage after successful auth
  const handleLogin = async (user, token, isNewUser = false) => {
    applyToken(token); // register interceptor synchronously before setting currentUser
    setCurrentUser(user);
    // Sync with backend to establish cookie session for legacy API routes
    if (token) {
      try {
        await axios.post(`${API_URL}/api/auth/supabase-sync`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
      } catch { /* ignore */ }
    }
    // Only redirect to onboarding for new signups, not existing users logging in
    if (isNewUser && !user.orgId) {
      window.location.href = '/onboarding';
    }
  };

  // Called when OnboardingPage creates an org — refresh user profile
  const handleOrgCreated = async (org) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      clearUserCache();

      let accessToken = null;
      try {
        const key = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
        if (key) accessToken = JSON.parse(localStorage.getItem(key))?.access_token;
      } catch (_) {}

      if (accessToken) {
        const user = await buildCurrentUser({ id: currentUser.id, email: currentUser.email });
        setCurrentUser(user);
      }
    } catch (err) {
      console.error('[handleOrgCreated] Error:', err);
    }
  };

  const handleLogout = async () => {
    // Clear localStorage first so any ongoing auto-refresh can't restore the session
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('sb-'))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }
    clearUserCache();
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch { /* ignore */ }
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    // Hard redirect — ensures full re-init from clean state
    window.location.replace('/login');
  };

  if (!authChecked && !isEmbed) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (approvalStatus === 'pending') {
    return (
      <PendingApprovalScreen
        email={currentUser?.email}
        accessToken={accessToken}
        onApproved={() => window.location.reload()}
        onSignOut={async () => { await supabase.auth.signOut(); setApprovalStatus(null); window.location.replace('/login'); }}
      />
    );
  }

  if (approvalStatus === 'rejected') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'sans-serif' }}>
        <div style={{ maxWidth: 420, textAlign: 'center', padding: '48px 32px', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
          <h2 style={{ margin: '0 0 12px', color: '#dc2626', fontSize: 22 }}>Access Not Granted</h2>
          <p style={{ color: '#64748b', lineHeight: 1.6, margin: '0 0 24px' }}>
            Your access request was not approved. Please contact <a href="mailto:help@deal-nexa.com" style={{ color: '#0f172a' }}>help@deal-nexa.com</a> if you believe this is an error.
          </p>
          <button
            onClick={async () => { await supabase.auth.signOut(); setApprovalStatus(null); window.location.replace('/login'); }}
            style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', color: '#64748b', fontSize: 14 }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <AnalysisProvider>
    <Router>
      <Routes>
        {/* Embed route - no login required */}
        <Route
          path="/embed"
          element={<AppContent currentUser={null} onLogout={handleLogout} isEmbed={true} />}
        />

        {/* Platform Admin - hidden route, no nav link */}
        <Route path="/platform-admin" element={<PlatformAdmin />} />

        {/* Public routes */}
        <Route path="/" element={<Navigate to={currentUser ? '/home' : '/login'} replace />} />
        <Route
          path="/login"
          element={currentUser ? <Navigate to="/home" /> : <LoginPage onLogin={handleLogin} />}
        />
        <Route
          path="/signup"
          element={currentUser ? <Navigate to="/home" /> : <SignupPage onLogin={handleLogin} />}
        />

        {/* Onboarding — accessible when logged in but no org yet */}
        <Route
          path="/onboarding"
          element={
            !currentUser ? (
              <Navigate to="/login" />
            ) : currentUser.orgId ? (
              <Navigate to="/home" />
            ) : (
              <OnboardingPage currentUser={currentUser} onOrgCreated={handleOrgCreated} />
            )
          }
        />

        {/* Terms */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/approve-user" element={<ApproveUserPage />} />
        <Route path="/reject-user"  element={<ApproveUserPage />} />

        {/* Auth callback from Google OAuth */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Invite accept — public (handles its own auth) */}
        <Route
          path="/invite/:token"
          element={<InvitePage currentUser={currentUser} onLogin={handleLogin} />}
        />

        {/* Deal Layer portal — no org auth required, uses portal session token */}
        <Route path="/portal/accept" element={<PortalAcceptPage />} />
        <Route path="/portal/login" element={<PortalAcceptPage />} />
        <Route path="/portal" element={<PortalPage />} />

        {/* CPA reviewer portal — token-gated, no org auth required */}
        <Route path="/cpa/review" element={<CpaReviewerPage />} />
        <Route path="/cpa/login"  element={<CpaReviewerPage />} />

        {/* Protected app routes */}
        <Route
          path="/*"
          element={
            !currentUser ? (
              <Navigate to="/login" />
            ) : !currentUser.orgId ? (
              <Navigate to="/onboarding" />
            ) : (
              <AppContent currentUser={currentUser} onLogout={handleLogout} />
            )
          }
        />
      </Routes>
    </Router>
    </AnalysisProvider>
  );
}

export default App;
